import {jobFetcher, JobsRes, JobProps, EducationsRes} from './jobs.fetch';
import useSWR from 'swr';
import {useSharedValue} from '@epic-core/hooks';
import {AppSharedKeys} from 'epic-ue-shared/dist/shared/sharedKeys';

export const createKey = (request?: Request): string => {
    if (!request) return '';
    return JSON.stringify(request);
};

export const sanitizeRequest = (request, hideRemoteFilter) => {
    //remove invalid values
    if (request?.state === '/') {
        delete request.state;
    }
    if (hideRemoteFilter && request?.remote) {
        delete request.remote;
    }
    return request;
};

interface Request {
    [key: string]: string | number | boolean;
}

interface JobsStore {
    jobData?: JobsRes;
    serverQuery?: {[key: string]: string};
    job?: JobProps;
}

export interface JobResultsRes {
    jobs?: JobsRes;
    isLoading: boolean;
    error?: string;
}

export interface JobRes {
    job?: JobProps;
    isLoading: boolean;
    error?: string;
}

export interface EducationRes {
    educations?: EducationsRes;
    isLoading: boolean;
    error?: string;
}

const defaultRequest = {skip: 0};

export const useJobResults = (request?: Request, hideRemoteFilter?: boolean): JobResultsRes => {
    const [jobsStore] = useSharedValue<JobsStore>(AppSharedKeys.JobsStore);
    const sharedValueJobs = jobsStore.jobData;
    const sharedValueQuery = jobsStore.serverQuery;

    let sameRequestAsServer = false;
    try {
        sameRequestAsServer = JSON.stringify(request) === JSON.stringify(sharedValueQuery);
    } catch (e) {
        //noop
    }

    const serverJobsValid = sharedValueJobs && sharedValueJobs.hits && sharedValueJobs.hits.length;
    const shouldFetch = !sameRequestAsServer || !serverJobsValid;

    const {data, error} = useSWR(
        shouldFetch
            ? createKey(sanitizeRequest(request, hideRemoteFilter) || defaultRequest)
            : null,
        jobFetcher.getJobs,
        {
            errorRetryCount: 1,
            revalidateOnFocus: false
        }
    );

    return {
        jobs: (!shouldFetch && sharedValueJobs) || data,
        isLoading: !data && !error,
        error
    };
};

export const useJob = (jobId: string): JobRes => {
    const id = `${jobId}`;

    const {data, error} = useSWR(id, jobFetcher.getJob, {
        errorRetryCount: 1,
        loadingTimeout: 2000,
        refreshInterval: 30000
    });

    return {
        job: data,
        isLoading: !data && !error,
        error
    };
};

export const useEducation = (jobId: string, type: string, page = 1, searchTerm = ''): EducationRes => {
    const {data, error} = useSWR([jobId, type, page, searchTerm], jobFetcher.getEducation, {
        errorRetryCount: 1,
        loadingTimeout: 2000,
        revalidateOnFocus: false
    });

    return {
        educations: data,
        isLoading: !data && !error,
        error
    };
};
